import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Award, Lock } from "lucide-react";

/**
 * AchievementBadges (iter71) — auto-awarded trophies. Renders a grid of
 * medal SVGs; earned = coloured by tier (bronze/silver/gold), locked = grey
 * with a padlock. Compact mode hides the locked list (used on the dashboard).
 */
const TIER_COLOR = {
  bronze: "#B87333",
  silver: "#C0C0C0",
  gold: "#FFD700",
};

export default function AchievementBadges({ studentId, sport, compact = false }) {
  const [data, setData] = useState({ earned: [], locked: [] });
  const [loading, setLoading] = useState(true);
  // Iter72: "just earned" toast — client-side diff against a per-(student, sport)
  // localStorage key. First-ever mount seeds the baseline WITHOUT toasting so
  // returning users on a fresh device don't get flooded on login.
  const seenKey = `rally_seen_badges_${studentId}_${sport}`;
  const initialisedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/students/${studentId}/achievements`, { params: { sport } })
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
        // Diff against last-seen list
        const earnedIds = (r.data?.earned || []).map((b) => b.id);
        let seen = [];
        try {
          const raw = window.localStorage.getItem(seenKey);
          seen = raw ? JSON.parse(raw) : null;
        } catch { seen = null; }
        if (seen === null) {
          // First-ever mount for this (student, sport). Seed silently.
          try { window.localStorage.setItem(seenKey, JSON.stringify(earnedIds)); } catch { /* ignore */ }
          initialisedRef.current = true;
          return;
        }
        const seenSet = new Set(seen);
        const fresh = (r.data?.earned || []).filter((b) => !seenSet.has(b.id));
        if (fresh.length) {
          fresh.forEach((b, i) => {
            setTimeout(() => {
              toast.success(`🏆 Just earned: ${b.name}`, {
                description: b.description,
                duration: 6000,
              });
            }, i * 700); // stagger so multiple pops don't overlap
          });
        }
        try { window.localStorage.setItem(seenKey, JSON.stringify(earnedIds)); } catch { /* ignore */ }
        initialisedRef.current = true;
      })
      .catch(() => { if (!cancelled) setData({ earned: [], locked: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [studentId, sport, seenKey]);

  if (loading) return null;
  const { earned = [], locked = [] } = data;
  const hasAny = earned.length + locked.length > 0;
  if (!hasAny) return null;

  return (
    <div className="card-surface p-6 fade-up" data-testid="achievement-badges">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Award className="w-4 h-4 text-[#FFD700]" />
          <div className="label-mono">Support badges</div>
        </div>
        <div className="flex items-center gap-3">
          <div className="label-mono text-[10px] text-[#A3A3A3]">
            {earned.length} earned · {locked.length} to unlock
          </div>
          <Link
            to="/badges"
            data-testid="badges-see-all"
            className="label-mono text-[10px] text-[#CCFF00] hover:underline"
          >SEE ALL →</Link>
        </div>
      </div>

      {earned.length === 0 && (
        <div className="text-xs text-[#A3A3A3] mb-4" data-testid="badges-empty">
          No badges yet — attend your first session to earn <span className="text-[#B87333] font-semibold">First Session</span>.
        </div>
      )}

      <div className={`grid ${compact ? "grid-cols-4 sm:grid-cols-6" : "grid-cols-3 sm:grid-cols-4 md:grid-cols-6"} gap-3`}>
        {earned.map((b) => (
          <BadgeMedal key={b.id} badge={b} earned />
        ))}
        {!compact && locked.map((b) => (
          <BadgeMedal key={b.id} badge={b} earned={false} />
        ))}
      </div>
    </div>
  );
}

function BadgeMedal({ badge, earned }) {
  const color = earned ? (TIER_COLOR[badge.tier] || "#B87333") : "#525252";
  const bg = earned ? `${color}22` : "transparent";
  return (
    <div
      className="text-center border p-3 transition-colors"
      style={{ borderColor: earned ? color : "#262626", background: bg }}
      data-testid={`badge-${badge.id}`}
      title={badge.description + (earned ? "" : ` · ${badge.requirement || ""}`)}
    >
      <div className="mx-auto mb-2 relative" style={{ width: 42, height: 42 }}>
        <svg viewBox="0 0 44 44" width="42" height="42">
          {/* Ribbon */}
          <path d="M 15 34 L 10 44 L 22 38 L 34 44 L 29 34 Z" fill={earned ? color : "#262626"} opacity={0.6} />
          {/* Medallion */}
          <circle cx="22" cy="20" r="16" fill={earned ? color : "#141414"} stroke={color} strokeWidth="2" />
          {/* Star or lock */}
          {earned ? (
            <polygon
              points="22,10 24.5,17.5 32,17.5 26,22 28.5,29.5 22,25 15.5,29.5 18,22 12,17.5 19.5,17.5"
              fill="#0A0A0A"
              opacity="0.85"
            />
          ) : (
            <g transform="translate(15,13)">
              <rect x="2" y="7" width="10" height="8" rx="1" fill="#525252" />
              <path d="M 4 7 V 4 a 3 3 0 0 1 6 0 v 3" fill="none" stroke="#525252" strokeWidth="1.5" />
            </g>
          )}
        </svg>
        {!earned && (
          <Lock className="w-3 h-3 absolute -bottom-1 -right-1 text-[#525252] bg-[#0A0A0A] rounded-full p-0.5" />
        )}
      </div>
      <div className="text-[10px] font-bold leading-tight" style={{ color: earned ? color : "#525252" }}>
        {badge.name}
      </div>
    </div>
  );
}
